import { hiconToHoyluUiIconsCentered, Icon, log } from "@hoylu/client-common";
import {
  type DisplayMode,
  initialQueryState,
  NexusInsightsBookmarks,
  NexusInsightsPanel,
  NexusInsightsPanelHeader,
  NexusInsightsProjectContextProvider,
  NexusInsightsShareActions,
  NexusInsightsShell,
  queryReducer,
} from "@hoylu/nexus-insights-panel";
import {
  CalendarService,
  HistoryService,
  ImportService,
  ReportService,
  TaskRelationsService,
  TaskService,
} from "@hoylu/nexus-service";
import { UserSettingsContext } from "@hoylu/user-settings";
import React, { useContext, useMemo, useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { config } from "../../../state/config/config.selector";
import { user } from "../../../state/user/user.selector";
import {
  getAllProjectCollaborators,
  selectDashboardSelectedProject,
} from "../../../state/workspaces/projects.selector";
import { cancelDashboardOption } from "../../../state/workspaces/workspaces.actions";
import { selectWorkspaces } from "../../../state/workspaces/workspaces.selector";
import { useI18n } from "../../../utils/hooks/use.i18n";
import { Modal } from "../Modal";
import Styles from "./nexus.insights.panel.dialog.module.css";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

export const NexusInsightsPanelDialog = () => {
  "use memo";

  const t = useI18n("INSIGHTS_DIALOG.");

  const dispatch = useDispatch();
  const settingsContext = useContext(UserSettingsContext);

  const project = useSelector(selectDashboardSelectedProject);
  const workspaces = useSelector(selectWorkspaces);
  const collaborators = useSelector(getAllProjectCollaborators);
  const configState = useSelector(config);
  const userState = useSelector(user);

  const projectLabel = useMemo(
    () => ({
      icon: project?.icon
        ? hiconToHoyluUiIconsCentered(project?.icon)
        : "hoylu-ui-icons-task-with-checkmark",
      color: project?.color ?? "var(--theme-normal)",
    }),
    [project],
  );

  const workspaceOptions = useMemo(() => {
    return workspaces.map((workspace) => ({
      label: workspace.workspaceName ?? "Unnamed Workspace",
      value: workspace.workspaceId,
    }));
  }, [workspaces]);

  // Since project selector can return null, we use an empty GUID as a fallback to avoid errors, yet this modal should never be shown if there is no project selected
  const nexusReportingService = new ReportService({
    scopeId: project?.id ?? EMPTY_GUID,
    server: configState.serviceConfig.nexus,
    credentialProvider: () => userState,
    logger: log,
  });

  const nexusTaskService = new TaskService({
    scopeId: project?.id ?? EMPTY_GUID,
    server: configState.serviceConfig.nexus,
    credentialProvider: () => userState,
    logger: log,
  });

  const nexusTaskRelationsService = new TaskRelationsService({
    scopeId: project?.id ?? EMPTY_GUID,
    server: configState.serviceConfig.nexus,
    credentialProvider: () => userState,
    logger: log,
  });

  const nexusTaskHistoryService = new HistoryService({
    scopeId: project?.id ?? EMPTY_GUID,
    server: configState.serviceConfig.nexus,
    credentialProvider: () => userState,
    logger: log,
  });

  const nexusImportService = new ImportService({
    scopeId: project?.id ?? EMPTY_GUID,
    server: configState.serviceConfig.nexus,
    credentialProvider: () => userState,
    logger: log,
  });

  const nexusCalendarService = new CalendarService({
    scopeId: project?.id ?? EMPTY_GUID,
    server: configState.serviceConfig.nexus,
    credentialProvider: () => userState,
    logger: log,
  });

  const [displayMode, setDisplayMode] = useState<DisplayMode>("chart");
  const [queryState, queryDispatch] = useReducer(
    queryReducer,
    initialQueryState,
  );
  const localizationCode = settingsContext.userSettings?.currentLanguage;

  const close = () => {
    dispatch(cancelDashboardOption());
  };

  const openHomeApp = () => {
    window.open(
      `${window.location.origin}/project/${project?.id}/insights`,
      "_blank",
    );
  };

  return (
    <NexusInsightsProjectContextProvider
      color={projectLabel.color}
      icon={projectLabel.icon}
      name={project?.name ?? "Nexus Insights"}
      projectRole={project?.projectRole}
      scopeId={project?.id ?? ""}
    >
      <NexusInsightsShell
        localizationCode={localizationCode}
        nexusCalendarService={nexusCalendarService}
        nexusImportService={nexusImportService}
        nexusReportingService={nexusReportingService}
        nexusTaskHistoryService={nexusTaskHistoryService}
        nexusTaskRelationsService={nexusTaskRelationsService}
        nexusTaskService={nexusTaskService}
        projectUsers={collaborators}
        query={{ state: queryState, dispatch: queryDispatch }}
        workspaceOptions={workspaceOptions}
      >
        <Modal
          allowContentToOverflow={false}
          components={{
            Header: (
              <NexusInsightsPanelHeader
                center={
                  <NexusInsightsBookmarks
                    displayMode={displayMode}
                    setDisplayMode={setDisplayMode}
                  />
                }
                right={
                  <>
                    <NexusInsightsShareActions displayMode={displayMode} />
                    <Icon
                      icon="hoylu-ui-icons-link-from-task"
                      onClick={openHomeApp}
                      title={t("OPEN_IN_NEW_WINDOW")}
                    />
                    <Icon
                      icon="hoylu-ui-icons-incomplete"
                      onClick={close}
                      title={t("CLOSE")}
                    />
                  </>
                }
                showAccentBorder
              />
            ),
          }}
          containerClassName={Styles.modalContainer}
          handleOutsideClick={close}
          showOverlay={true}
        >
          <NexusInsightsPanel
            displayMode={displayMode}
            localizationCode={localizationCode}
          />
        </Modal>
      </NexusInsightsShell>
    </NexusInsightsProjectContextProvider>
  );
};
